import {
  serveMarkerLandscape,
  serveMarkerPortrait,
  serverScore,
  serviceCourtCoords,
} from "../../../../gameLogic";
import { teamRawPlayerNames } from "../../../registry/utils/courtTeams";

export function courtServeTextAnchor(portrait, half, vbW, vbH, team) {
  const players = teamRawPlayerNames(team);
  const isDoubles = players.length > 1 || team?.type === "doubles";
  const slots = courtPlayerPositions(portrait, half, isDoubles);
  const pos = slots[0] || { x: vbW / 2, y: vbH / 2 };
  if (portrait) {
    return { x: vbW - 6, y: pos.y - 11 };
  }
  if (!isDoubles) {
    return { x: pos.x, y: 38 };
  }
  return { x: pos.x, y: pos.y - 30 };
}

export function courtServeAnchorClass(portrait) {
  return portrait ? "court-half-anchor-right" : "court-half-anchor-mid";
}

export function pctPos(x, y, vbW, vbH) {
  return { left: `${(x / vbW) * 100}%`, top: `${(y / vbH) * 100}%` };
}

/**
 * Map synced doubles names to court slot order.
 * `players` must be [right-service-box, left-service-box] on the serving side
 * (see syncTeamsToActiveGameBoxes); fixed registry order on the receiving side.
 * When ends have changed (interval / change of ends), mirror slots so court-left
 * stays court-left and the serve marker stays on the active server.
 */
export function doublesPlayersForCourtDisplay(
  portrait,
  players,
  courtContext = null
) {
  const list = (players || []).filter(Boolean);
  if (list.length < 2) return list;

  if (!courtContext?.side) {
    if (!portrait) return [list[1], list[0]];
    return list;
  }

  const { endsSwapped } = courtContext;
  let ordered = list;

  if (portrait) {
    if (endsSwapped) ordered = [list[1], list[0]];
  } else if (!endsSwapped) {
    ordered = [list[1], list[0]];
  }

  return ordered;
}

export function courtPlayerPositions(portrait, half, isDoubles) {
  if (portrait) {
    const y = half === "top" ? 90 : 349;
    if (isDoubles) return [{ x: 58, y }, { x: 142, y }];
    return [{ x: 100, y }];
  }
  const x = half === "left" ? 82 : 358;
  if (isDoubles) return [{ x, y: 62 }, { x, y: 138 }];
  return [{ x, y: 100 }];
}

/**
 * Resolve even/right court from engine serveCourt, or numeric score parity.
 * Non-numeric scores (tennis "15") without serveCourt default to even/right —
 * engines must pass serveCourt for ITF/PB.
 */
export function resolveServeCourtEven(serveCourt, serving, side, scoreA, scoreB) {
  if (serveCourt === "right" || serveCourt === "deuce") return true;
  if (serveCourt === "left" || serveCourt === "ad") return false;
  const raw =
    side === serving
      ? serverScore(serving, scoreA, scoreB)
      : side === "A"
        ? scoreA
        : scoreB;
  const n = Number(raw);
  if (!Number.isFinite(n)) return true;
  return n % 2 === 0;
}

/**
 * Singles stance:
 * - serviceBoxes (badminton / tennis / pickleball): player moves to L/R service box
 * - fixedEnds (table tennis): player stays centered — ITTF has no even/odd service courts
 */
export function singlesCourtPosition(
  portrait,
  side,
  serving,
  scoreA,
  scoreB,
  endsSwapped,
  serveCourt = null,
  playerStance = "serviceBoxes"
) {
  if (playerStance === "fixedEnds") {
    const half = portrait
      ? side === "A"
        ? endsSwapped
          ? "bottom"
          : "top"
        : endsSwapped
          ? "top"
          : "bottom"
      : side === "A"
        ? endsSwapped
          ? "right"
          : "left"
        : endsSwapped
          ? "left"
          : "right";
    return courtPlayerPositions(portrait, half, false)[0];
  }
  const even = resolveServeCourtEven(serveCourt, serving, side, scoreA, scoreB);
  return serviceCourtCoords(portrait, side, endsSwapped, even);
}

export function slotHasServeIndicator(
  portrait,
  side,
  serving,
  scoreA,
  scoreB,
  endsSwapped,
  slot,
  serveCourt = null,
  playerStance = "serviceBoxes",
  options = null
) {
  if (serving !== side) return false;

  // Table tennis doubles: serve always from right court (slot 0 after sync).
  if (playerStance === "fixedEnds" && options?.doublesDiagonal) {
    return options.slotIndex === 0;
  }

  // Table tennis singles: icon on the sole player (no L/R court).
  if (playerStance === "fixedEnds") {
    const serverIndex = options?.serverIndex;
    const slotIndex = options?.slotIndex;
    if (typeof serverIndex === "number" && typeof slotIndex === "number") {
      return slotIndex === serverIndex;
    }
    return true;
  }

  const even = resolveServeCourtEven(serveCourt, serving, side, scoreA, scoreB);
  const sm =
    serveCourt != null
      ? serviceCourtCoords(portrait, side, endsSwapped, even)
      : portrait
        ? serveMarkerPortrait(serving, scoreA, scoreB, endsSwapped)
        : serveMarkerLandscape(serving, scoreA, scoreB, endsSwapped);
  return Math.abs(slot.x - sm.x) <= 48 && Math.abs(slot.y - sm.y) <= 55;
}

export function serveNetOffsetClass(portrait, half) {
  if (portrait) return half === "top" ? "serve-net-down" : "serve-net-up";
  return half === "left" ? "serve-land-net-l" : "serve-land-net-r";
}

export function flipDoublesPartners(team) {
  const slots = team?.slots;
  if (slots?.length >= 2) {
    return { ...team, slots: [slots[1], slots[0]] };
  }
  const p = team?.players || [];
  if (p.length < 2) return team;
  return { ...team, players: [p[1], p[0]] };
}
